import {
  X, Phone, Mail, MapPin, Package, ShieldCheck,
  IndianRupee, Clock, Database, MessageSquare,
  Calendar, User, ArrowRight, CheckCircle2,
  AlertCircle, Building2, Tag,
} from "lucide-react";
import { formatDateTime } from "../../Component/Common/formatDateTime";
import ActivityTimeline from "./ActivityTimeline";
import { useState } from "react";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  brand:      "#2563EB",
  brandDark:  "#1D4ED8",
  brandSoft:  "#EFF6FF",
  brandBorder:"#BFDBFE",
  success:    "#10B981",
  successSoft:"#ECFDF5",
  warning:    "#D97706",
  warningSoft:"#FFFBEB",
  danger:     "#EF4444",
  dangerSoft: "#FEF2F2",
  slate50:    "#F8FAFC",
  slate100:   "#F1F5F9",
  slate200:   "#E2E8F0",
  slate400:   "#94A3B8",
  slate600:   "#475569",
  slate800:   "#1E293B",
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function initials(name = "") {
  return name.split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase() || "??";
}

function relativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const LEAD_TYPE_MAP  = { 2:"MCP", 4:"CP", 5:"ARP" };
const STATUS_COLORS  = {
  won:         { bg:"#ECFDF5", color:"#059669", border:"#A7F3D0" },
  lost:        { bg:"#FEF2F2", color:"#DC2626", border:"#FECACA" },
  new:         { bg:"#EFF6FF", color:"#2563EB", border:"#BFDBFE" },
  "in process":{ bg:"#FFFBEB", color:"#D97706", border:"#FDE68A" },
  followup:    { bg:"#F5F3FF", color:"#7C3AED", border:"#DDD6FE" },
};
const REMARK_STATUS_COLORS = {
  followup:{ bg:"#F5F3FF", color:"#7C3AED", border:"#DDD6FE", label:"Follow-up" },
  new:     { bg:"#EFF6FF", color:"#2563EB", border:"#BFDBFE", label:"New" },
  won:     { bg:"#ECFDF5", color:"#059669", border:"#A7F3D0", label:"Won" },
  lost:    { bg:"#FEF2F2", color:"#DC2626", border:"#FECACA", label:"Lost" },
};

function StatusPill({ status }) {
  const cfg = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide"
      style={{ background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background:cfg.color }}/>
      {status}
    </span>
  );
}

// ─── ATOMS ────────────────────────────────────────────────────────────────────
function SectionCard({ title, icon: Icon, iconColor = T.brand, children }) {
  return (
    <div className="bg-white rounded-2xl border overflow-hidden"
      style={{ borderColor:T.slate200, boxShadow:"0 1px 6px rgba(0,0,0,0.05)" }}>
      <div className="flex items-center gap-2 px-5 py-2 border-b" style={{ borderColor:T.slate100, background:T.slate50 }}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background:`${iconColor}15` }}>
          <Icon size={14} style={{ color:iconColor }}/>
        </div>
        <h3 className="text-[13px] font-bold" style={{ color:T.slate800 }}>{title}</h3>
      </div>
      <div className="px-5 py-2">{children}</div>
    </div>
  );
}

function InfoRow({ label, value, icon: Icon, full, highlight }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className={full ? "col-span-2" : ""}>
      <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest mb-0.5"
        style={{ color:T.slate400 }}>
        {Icon && <Icon size={9}/>} {label}
      </p>
      <div className="text-[13px] font-semibold break-words"
        style={{ color: highlight ? T.brand : T.slate800 }}>
        {value}
      </div>
    </div>
  );
}

function InfoGrid({ children }) {
  return <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>;
}

// ─── ACTIVITY ITEM (matches reference image) ──────────────────────────────────
function ActivityItem({ remark, isLast }) {
  const user        = remark.followup_user || remark.created_user || {};
  const userName    = user.first_name
    ? `${user.first_name} ${user.last_name || ""}`.trim()
    : "System";
  const userInitials = initials(userName);
  const statusCfg   = REMARK_STATUS_COLORS[remark.status] || REMARK_STATUS_COLORS.new;
  const createdAt   = remark.created_at;
  const followupAt  = remark.followup_date;

  return (
    <div className="flex gap-3">
      {/* Left: avatar + connector line */}
      <div className="flex flex-col items-center">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-bold text-white shrink-0"
          style={{ background:`linear-gradient(135deg,${T.brand},${T.brandDark})` }}
        >
          {user.photo_url
            ? <img src={user.photo_url} alt={userName} className="w-full h-full rounded-full object-cover"/>
            : userInitials
          }
        </div>
        {!isLast && (
          <div className="w-px flex-1 mt-2" style={{ background:T.slate200, minHeight:24 }}/>
        )}
      </div>

      {/* Right: content */}
      <div className={`flex-1 min-w-0 ${isLast ? "" : "pb-5"}`}>
        {/* Header row */}
        <div className="flex items-center flex-wrap gap-2 mb-0.5">
          <span className="text-[13px] font-bold" style={{ color:T.slate800 }}>
            {userName}
          </span>
          {user.position && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
              style={{ background:T.brandSoft, color:T.brand }}>
              {user.position}
            </span>
          )}
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background:statusCfg.bg, color:statusCfg.color, border:`1px solid ${statusCfg.border}` }}
          >
            {statusCfg.label}
          </span>
        </div>

        {/* Timestamp */}
        <p className="text-[11px] flex items-center gap-1.5 mb-2" style={{ color:T.slate400 }}>
          <Clock size={10}/>
          {new Date(createdAt).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}
          <span>·</span>
          {new Date(createdAt).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" })}
          <span className="ml-1 text-[10px]" style={{ color:T.slate400 }}>
            ({relativeTime(createdAt)})
          </span>
        </p>

        {/* Remark message card — matches reference "quoted block" */}
        {remark.remark && (
          <div
            className="rounded-xl px-4 py-3 mb-2"
            style={{
              background:T.slate50,
              border:`1px solid ${T.slate200}`,
              borderLeft:`3px solid ${T.brand}`,
            }}
          >
            <p className="text-[13px] leading-relaxed" style={{ color:T.slate800 }}>
              {remark.remark}
            </p>
          </div>
        )}

        {/* Follow-up date pill */}
        {followupAt && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold"
            style={{ background:T.warningSoft, color:T.warning, border:`1px solid #FDE68A` }}>
            <Calendar size={10}/>
            Follow-up: {new Date(followupAt).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}{" "}
            at {new Date(followupAt).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ROOT: ViewLeadModal
// ─────────────────────────────────────────────────────────────────────────────
export function ViewLeadModal({ lead, onClose }) {

  const [remarks, setRemarks] = useState(
    lead.remarks || []
  );
  if (!lead) return null;

  const PRODUCT_META = {
    warranty:   { label:"Warranty",   bg:T.brandSoft, color:T.brand },
    emi_locker: { label:"EMI Locker", bg:"#F5F3FF",   color:"#7C3AED" },
  };

  const rawProducts  = typeof lead.products === "object" ? lead.products?.products || lead.products : lead.products;
  const productList  = (Array.isArray(rawProducts) ? rawProducts.join(",") : rawProducts || "")
    .split(",").map(s => s.trim()).filter(Boolean);

  

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-2"
      style={{ background:"rgba(15,23,42,0.6)", backdropFilter:"blur(4px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white w-full sm:max-w-7xl sm:rounded-3xl overflow-hidden flex flex-col"
        style={{
          maxHeight:"94vh",
          boxShadow:"0 24px 64px rgba(0,0,0,0.2)",
          borderRadius:"24px 24px 0 0",
        }}
      >

        {/* ── HEADER ── */}
        <div
          className="flex items-start justify-between px-6 py-1 shrink-0"
          style={{ background:`linear-gradient(135deg,#1E3A8A,${T.brand})` }}
        >
          <div>
            <div className="flex items-center gap-3 ">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background:"rgba(255,255,255,0.15)" }}>
                <Building2 size={17} color="white"/>
              </div>
              <h2 className="text-white font-black text-[16px] leading-tight">Lead Details</h2>
            </div>
            <div className="flex items-center gap-2 ml-12 flex-wrap">
              <span className="text-[11px] font-bold text-blue-200">
                {lead.lead_code}
              </span>
              <span className="text-blue-300">·</span>
              <StatusPill status={lead.status}/>
              <span className="text-blue-300">·</span>
              <span className="text-[11px] text-blue-200 font-semibold">
                {LEAD_TYPE_MAP[lead.lead_type] || "—"}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-white/20 transition-colors shrink-0"
          >
            <X size={15} color="white"/>
          </button>
        </div>

        {/* ── SCROLLABLE BODY ── */}
        <div className="flex-1 overflow-y-auto p-2 space-y-4" style={{ background:T.slate50 }}>

          {/* Row 1: Business + Location */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

            <div className="grid grid-rows-2 gap-2">
              <SectionCard title="Business & Contact" icon={Building2} iconColor={T.brand}>
              <InfoGrid>
                <InfoRow label="Business Name"  value={lead.name}        full highlight/>
                <InfoRow label="Owner Name"     value={lead.owner_name}/>
                <InfoRow label="Phone"          value={lead.phone}        icon={Phone}/>
                <InfoRow label="Email"          value={lead.email}        icon={Mail}/>
              </InfoGrid>
            </SectionCard>

            <SectionCard title="Location" icon={MapPin} iconColor="#059669">
              <InfoGrid>
                <InfoRow label="State"          value={lead.state}/>
                <InfoRow label="District"       value={lead.district}/>
                <InfoRow label="Pincode"        value={lead.pincode}/>
                {/* <InfoRow label="State Code"     value={lead.state_in}/> */}
                {/* <InfoRow label="District Code"  value={lead.district_in}/> */}
                <InfoRow label="Address"
                  value={[lead.address1, lead.address2].filter(Boolean).join(", ")}
                  full/>
              </InfoGrid>
            </SectionCard>
            </div>

            <div className="grid grid-rows-2 gap-2">
              <SectionCard title="Package & Financials" icon={Package} iconColor="#D97706">
              <InfoGrid>
                <InfoRow label="Package Name"   value={lead.package_name}/>
                <InfoRow label="Badge Name"     value={lead.badge_name}/>
                {/* <InfoRow label="Benefits"       value={lead.benefits}/> */}
                {/* <InfoRow label="Eligibility"    value={lead.eligibility}/> */}
                <InfoRow label="Lead Amount"    value={lead.lead_amount} icon={IndianRupee}/>
              </InfoGrid>

              <div className="flex gap-3">
                {/* Products */}
              {productList.length > 0 && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor:T.slate100 }}>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color:T.slate400 }}>
                    Products
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {productList.map((item, i) => {
                      const meta = PRODUCT_META[item] || { label:item, bg:T.slate100, color:T.slate600 };
                      return (
                        <span key={i} className="px-2.5 py-1 rounded-full text-[11px] font-bold"
                          style={{ background:meta.bg, color:meta.color }}>
                          {meta.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Payment */}
              <div className="mt-4 pt-4 border-t" style={{ borderColor:T.slate100 }}>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color:T.slate400 }}>
                  Payment Method
                </p>
                <div className="flex gap-2 flex-wrap">
                  {lead.pay_now === 1 && (
                    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold"
                      style={{ background:T.successSoft, color:T.success }}>
                      Pay Now
                    </span>
                  )}
                  {lead.pay_later === 1 && (
                    <span className="px-2.5 py-1 rounded-full text-[11px] font-bold"
                      style={{ background:T.warningSoft, color:T.warning }}>
                      Pay Later
                    </span>
                  )}
                  {!lead.pay_now && !lead.pay_later && (
                    <span className="text-[12px] italic" style={{ color:T.slate400 }}>Not set</span>
                  )}
                </div>
              </div>
              </div>
            </SectionCard>

            <SectionCard title="System & Audit" icon={Database} iconColor="#7C3AED">
              <InfoGrid>
                <InfoRow label="Lead ID"        value={lead.id}/>
                <InfoRow label="Lead Type"      value={LEAD_TYPE_MAP[lead.lead_type] || "—"}/>
                <InfoRow label="Company Code"   value={lead.company_code}/>
                <InfoRow label="Agent ID"       value={lead.agent_id}/>
                <InfoRow label="Manager ID"     value={lead.manager_id}/>
                <InfoRow label="Created By"     value={lead.created_by_name}/>
                <InfoRow label="Updated By"     value={lead.updated_by_name}/>
                <InfoRow label="Created At"     value={formatDateTime(lead.created_at)} icon={Clock}/>
                <InfoRow label="Updated At"     value={formatDateTime(lead.updated_at)} icon={Clock}/>
              </InfoGrid>
            </SectionCard>
            </div>

            <SectionCard title="Activity Timeline" icon={MessageSquare} iconColor={T.brand}>
             <ActivityTimeline
              remarks={remarks}
              leadId={lead.id}
              leadStatus={lead.status}
              // followUpById={lead.follow_up_by}
              onRemarkAdded={(newRemark) => {

                const remarkData =
                  newRemark?.data ||
                  newRemark;

                setRemarks((prev) => [
                  remarkData,
                  ...prev,
                ]);
              }}
              maxHeight={430}
            />

          </SectionCard>
          
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div className="flex items-center justify-between px-6 py-4 border-t shrink-0"
          style={{ borderColor:T.slate100, background:"white" }}>
          <p className="text-[11px]" style={{ color:T.slate400 }}>
            Lead · {lead.lead_code}
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-[13px] font-black text-white transition-all hover:scale-105 active:scale-95"
            style={{
              background:`linear-gradient(135deg,${T.brand},${T.brandDark})`,
              boxShadow:`0 4px 14px ${T.brand}44`,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── unchanged helper for backward compat ── */
function Card({ title, icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        {icon && <span className="text-blue-600">{icon}</span>}
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">{children}</div>
    </div>
  );
}

function Info({ label, value, full, icon }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-xs text-slate-500 flex items-center gap-1">
        {icon && <span className="text-slate-400">{icon}</span>}
        {label}
      </p>
      <div className="text-sm font-medium text-slate-800 break-words">{value}</div>
    </div>
  );
}

// import {
//   X,
//   Phone,
//   Mail,
//   MapPin,
//   Package,
//   ShieldCheck,
//   IndianRupee,
//   Clock,
//   Database,
// } from "lucide-react";
// import { formatDateTime } from "../../Component/Common/formatDateTime";

// export function ViewLeadModal({ lead, onClose }) {

//   const LEAD_TYPE_MAP = {
//     2: "Company",
//     4: "Agent",
//     5: "Retailer",
//   };

//  const PRODUCT_META = {
//   warranty: {
//     label: "Warranty",
//     className: "bg-blue-100 text-blue-700",
//   },
//   emi_locker: {
//     label: "EMI Locker",
//     className: "bg-purple-100 text-purple-700",
//   },
// };

// const rawProducts =
//   typeof lead.products === "object"
//     ? lead.products?.products || lead.products
//     : lead.products;

// const productString = Array.isArray(rawProducts)
//   ? rawProducts.join(",")
//   : rawProducts;
  
//   if (!lead) return null;

//   return (
//     <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-3">
//       <div className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">

//         {/* ================= HEADER ================= */}
//         <div className="flex justify-between items-center px-6 py-4 border-b bg-slate-50 shrink-0">
//           <div>
//             <div className="flex gap-2">
//               <h2 className="text-lg font-semibold text-slate-900">
//               Lead Details
//             </h2>

//             <span
//                 className={`px-4 py-2 mb-2 rounded-full text-xs font-semibold capitalize ${
//                   lead.status === "won"
//                     ? "bg-green-100 text-green-700"
//                     : lead.status === "lost"
//                     ? "bg-red-100 text-red-700"
//                     : lead.status === "in progress"
//                     ? "bg-amber-100 text-amber-700"
//                     : "bg-blue-100 text-blue-700"
//                 }`}
//               >
//                 {lead.status}
//               </span>
//             </div>
//             <p className="text-xs text-slate-500">
//               Lead Code:{" "}
//               <span className="font-semibold">{lead.lead_code}</span>
//             </p>
//           </div>

//           <button
//             onClick={onClose}
//             className="p-2 rounded-full hover:bg-slate-200 transition"
//           >
//             <X className="w-5 h-5 text-slate-700" />
//           </button>
//         </div>

//         {/* ================= BODY (SCROLLABLE) ================= */}
//         <div className="grid grid-cols-1 sm:grid-cols-2 overflow-y-auto gap-3 px-6 py-6 bg-slate-50 ">

//           {/* BUSINESS */}
//           <Card title="Business & Contact" icon={<ShieldCheck />}>
//             <Info label="Business Name" value={lead.name} />
//             <Info label="Owner Name" value={lead.owner_name} />
//             <Info icon={<Phone size={14} />} label="Phone" value={lead.phone} />
//             <Info icon={<Mail size={14} />} label="Email" value={lead.email} />
//           </Card>

//           {/* LOCATION */}
//           <Card title="Location" icon={<MapPin />}>
//             <Info label="State" value={lead.state} />
//             <Info label="District" value={lead.district} />
//             <Info label="Pincode" value={lead.pincode} />
//             <Info label="State Code" value={lead.state_in} />
//             <Info label="District Code" value={lead.district_in} />
//             <Info label="Address" value={lead.address_full} full />
//           </Card>

//           {/* PACKAGE & FINANCIAL */}
//           <Card title="Package & Financials" icon={<Package />}>
//             <Info label="Package Name" value={lead.package_name} />
//             {/* <Info label="Package ID" value={lead.package_id} /> */}
//             <Info label="Badge Name" value={lead.badge_name} />
//             {/* <Info label="Badge ID" value={lead.badge_id} /> */}
//             <Info label="Benefits" value={lead.benefits} />
//             <Info label="Eligibility" value={lead.eligibility} />

//             <Info
//               icon={<IndianRupee size={14} />}
//               label="Lead Amount"
//               value={lead.lead_amount}
//             />

//             {/* PAYMENT METHOD */}
//             <div className="">
//               <p className="text-xs text-slate-500 mb-1">Payment Method</p>

//               {lead.pay_now === 1 || lead.pay_later === 1 ? (
//                 <div className="flex gap-2 flex-wrap">
//                   {lead.pay_now === 1 && (
//                     <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
//                       Pay Now
//                     </span>
//                   )}

//                   {lead.pay_later === 1 && (
//                     <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
//                       Pay Later
//                     </span>
//                   )}
//                 </div>
//               ) : (
//                 <span className="text-xs font-medium text-slate-400 italic">
//                   Payment method not added
//                 </span>
//               )}
//             </div>
//           </Card>


//           {/* STATUS */}
//           {lead.status !== "won" && (
//             <Card title="Lead Status">
//             <div className="flex items-center gap-3 md:col-span-2">
//               <span className="text-sm font-medium text-slate-600">
//                 {lead.status}
//               </span>
              
//             </div>

//             {lead.remark && (
//               <div className="md:col-span-2">
//                 <p className="text-xs font-semibold text-slate-500 mb-1">
//                   Remark
//                 </p>
//                 <p className="text-sm text-slate-700 bg-white border rounded-xl p-3">
//                   {lead.remark}
//                 </p>
//               </div>
//             )}
//           </Card>
//           )}

//           {/* SYSTEM / AUDIT */}
//           <Card title="System & Audit Info" icon={<Database />}>
//             <Info label="Lead ID" value={lead.id} />
//             <Info
//               label="Lead Type"
//               value={LEAD_TYPE_MAP[lead.lead_type] || "-"}
//             />

//             <Info
//               label="Lead For"
//               value={
//                 productString ? (
//                   <div className="flex flex-wrap gap-2">
//                     {productString.split(",").map((item, i) => {
//                       const key = item.trim();
//                       const meta = PRODUCT_META[key] || {
//                         label: key,
//                         className: "bg-gray-100 text-gray-700",
//                       };

//                       return (
//                         <span
//                           key={i}
//                           className={`px-3 py-1 text-xs font-semibold rounded-full ${meta.className}`}
//                         >
//                           {meta.label}
//                         </span>
//                       );
//                     })}
//                   </div>
//                 ) : (
//                   "-"
//                 )
//               }
//             />
//             <Info label="Company Code" value={lead.company_code} />
//             <Info label="Agent ID" value={lead.agent_id} />
//             <Info label="Manager ID" value={lead.manager_id} />
//             <Info label="Created By" value={lead.created_by_name} />
//             <Info label="Updated By" value={lead.updated_by_name} />

//             <Info
//               icon={<Clock size={14} />}
//               label="Created At"
//               value={formatDateTime(lead.created_at)}
//             />
//             <Info
//               icon={<Clock size={14} />}
//               label="Updated At"
//               value={formatDateTime(lead.updated_at)}
//             />
//           </Card>
//         </div>

//         {/* ================= FOOTER ================= */}
//         <div className="px-6 py-4 border-t bg-white shrink-0 flex justify-end">
//           <button
//             onClick={onClose}
//             className="px-6 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition"
//           >
//             Close
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ================= REUSABLE COMPONENTS ================= */

// function Card({ title, icon, children }) {
//   return (
//     <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
//       <div className="flex items-center gap-2 mb-4">
//         {icon && <span className="text-blue-600">{icon}</span>}
//         <h3 className="text-sm font-semibold text-slate-800">
//           {title}
//         </h3>
//       </div>

//       {/* STRICT 2-COLUMN GRID */}
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
//         {children}
//       </div>
//     </div>
//   );
// }

// function Info({ label, value, full, icon }) {
//   if (value === null || value === undefined || value === "") return null;

//   return (
//     <div className={full ? "md:col-span-2" : ""}>
//       <p className="text-xs text-slate-500 flex items-center gap-1">
//         {icon && <span className="text-slate-400">{icon}</span>}
//         {label}
//       </p>

//       <div className="text-sm font-medium text-slate-800 break-words">
//         {value}
//       </div>
//     </div>
//   );
// }

